import React, { useEffect, useState } from "react";
import { Clock, Eye, Lock, RefreshCw, Save, ShieldCheck, Smartphone, TrendingDown, TrendingUp, Type } from "lucide-react";
import AdminLayout from "./AdminLayout";
import API from "../services/api";

const weekDays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function AdminSettings() {
  const admin = (() => {
    try { return JSON.parse(localStorage.getItem("admin") || "null"); } catch { return null; }
  })();

  const [form, setForm] = useState({
    min_bet: "10",
    max_bet: "10000",
    bet_ank_limit: "10",
    min_deposit: "300",
    min_withdrawal: "500",
    withdraw_limit_per_day: "1",
    withdraw_from_time: "10:00",
    withdraw_to_time: "18:00",
    withdraw_days: "Mon,Tue,Wed,Thu,Fri,Sat,Sun",
    whatsapp_number: "",
    home_running_notice: "",
    show_casino_card: "1",
    show_demo_login: "1",
  });
  const [passwordForm, setPasswordForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [saving, setSaving] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);
  const [googleAuth, setGoogleAuth] = useState(null);
  const [gaLoading, setGaLoading] = useState(false);
  const [gaRegen, setGaRegen] = useState(false);

  useEffect(() => {
    API.get("/admin/settings")
      .then((res) => { setForm((c) => ({ ...c, ...(res.data || {}) })); })
      .catch(() => {});
    loadGoogleAuth();
  }, []);

  const loadGoogleAuth = async () => {
    setGaLoading(true);
    try {
      const res = await API.get("/admin/google-auth");
      setGoogleAuth(res.data);
    } catch {
      setGoogleAuth(null);
    } finally {
      setGaLoading(false);
    }
  };

  const setupGoogleAuth = async () => {
    setGaRegen(true);
    try {
      const res = await API.post("/admin/google-auth/regenerate");
      setGoogleAuth(res.data);
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to set up 2FA");
    } finally {
      setGaRegen(false);
    }
  };

  const regenerateGoogleAuth = async () => {
    if (!window.confirm("This will invalidate the current 2FA QR code. Continue?")) return;
    setGaRegen(true);
    try {
      const res = await API.post("/admin/google-auth/regenerate");
      setGoogleAuth(res.data);
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to regenerate");
    } finally {
      setGaRegen(false);
    }
  };

  const disableGoogleAuth = async () => {
    if (!window.confirm("This will disable 2FA. Admin login will no longer require a code. Continue?")) return;
    setGaRegen(true);
    try {
      await API.post("/admin/google-auth/disable");
      setGoogleAuth({ enabled: false });
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to disable 2FA");
    } finally {
      setGaRegen(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      // Exclude welcome-bonus keys — those are managed by AdminBonus page
      const { signup_bonus, signup_bonus_enabled, signup_bonus_cutoff_user_id, ...safeForm } = form;
      await API.post("/admin/settings", safeForm);
      alert("Settings saved");
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to save settings");
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async () => {
    if (!admin?.session_token) { alert("Admin session not found"); return; }
    if (!passwordForm.current_password || !passwordForm.new_password || !passwordForm.confirm_password) {
      alert("Fill all password fields");
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      alert("New password and confirm password do not match");
      return;
    }
    setPwSaving(true);
    try {
      await API.post("/admin/change-password", {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });
      setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
      alert("Admin password changed");
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to change password");
    } finally {
      setPwSaving(false);
    }
  };

  return (
    <AdminLayout>
      <div style={page}>
        <div style={pageHeader}>
          <div>
            <h1 style={title}>Settings</h1>
            <p style={subtitle}>Platform configuration</p>
          </div>
          <button type="button" style={saveBtn} onClick={saveSettings} disabled={saving}>
            <Save size={16} />
            {saving ? "Saving..." : "Save Settings"}
          </button>
        </div>

        <div style={grid}>
          {/* Bet Limits */}
          <section style={card}>
            <div style={cardHead}>
              <div style={{ ...cardIcon, background: "rgba(249,115,22,0.12)" }}>
                <TrendingUp size={18} color="#f97316" />
              </div>
              <h3 style={cardTitle}>Bet Limits</h3>
            </div>
            <Field label="Min Bet Amount (Rs)" value={form.min_bet} onChange={(v) => setForm((c) => ({ ...c, min_bet: v }))} />
            <Field label="Max Bet Amount (Rs)" value={form.max_bet} onChange={(v) => setForm((c) => ({ ...c, max_bet: v }))} />
            <Field label="Max Ank Bets Per Session (count)" value={form.bet_ank_limit} placeholder="e.g. 5" onChange={(v) => setForm((c) => ({ ...c, bet_ank_limit: v }))} />
          </section>

          {/* Transaction Limits */}
          <section style={card}>
            <div style={cardHead}>
              <div style={{ ...cardIcon, background: "rgba(22,163,74,0.12)" }}>
                <TrendingDown size={18} color="#16a34a" />
              </div>
              <h3 style={cardTitle}>Transaction Limits</h3>
            </div>
            <Field label="Min Deposit (Rs)" value={form.min_deposit} onChange={(v) => setForm((c) => ({ ...c, min_deposit: v }))} />
            <Field label="Min Withdrawal (Rs)" value={form.min_withdrawal} onChange={(v) => setForm((c) => ({ ...c, min_withdrawal: v }))} />
            <FieldSelect
              label="Daily Withdraw Limit"
              value={form.withdraw_limit_per_day}
              onChange={(v) => setForm((c) => ({ ...c, withdraw_limit_per_day: v }))}
              options={[
                { value: "1", label: "Once per day" },
                { value: "2", label: "Twice per day" },
                { value: "0", label: "No limit" },
              ]}
            />
          </section>

          {/* Withdraw Schedule */}
          <section style={card}>
            <div style={cardHead}>
              <div style={{ ...cardIcon, background: "rgba(79,70,229,0.12)" }}>
                <Clock size={18} color="#4f46e5" />
              </div>
              <h3 style={cardTitle}>Withdraw Schedule</h3>
            </div>
            <div style={timeRow}>
              <label style={{ ...fieldWrap, flex: 1 }}>
                <span style={fieldLabel}>From Time</span>
                <input type="time" style={fieldInput} value={form.withdraw_from_time || "10:00"} onChange={(e) => setForm((c) => ({ ...c, withdraw_from_time: e.target.value }))} />
              </label>
              <label style={{ ...fieldWrap, flex: 1 }}>
                <span style={fieldLabel}>To Time</span>
                <input type="time" style={fieldInput} value={form.withdraw_to_time || "18:00"} onChange={(e) => setForm((c) => ({ ...c, withdraw_to_time: e.target.value }))} />
              </label>
            </div>
            <div style={fieldWrap}>
              <span style={fieldLabel}>Allowed Days</span>
              <div style={dayGrid}>
                {weekDays.map((day) => {
                  const selected = (form.withdraw_days || "").split(",").map((d) => d.trim()).includes(day);
                  return (
                    <button
                      key={day}
                      type="button"
                      style={selected ? dayActive : dayBtn}
                      onClick={() => {
                        const current = (form.withdraw_days || "").split(",").map((d) => d.trim()).filter(Boolean);
                        const next = selected ? current.filter((d) => d !== day) : [...current, day];
                        setForm((c) => ({ ...c, withdraw_days: next.join(",") }));
                      }}
                    >
                      {day}
                    </button>
                  );
                })}
              </div>
            </div>
            <p style={hint}>Withdrawals can only be requested during the above time window and on selected days.</p>
          </section>

          {/* WhatsApp */}
          <section style={card}>
            <div style={cardHead}>
              <div style={{ ...cardIcon, background: "rgba(34,197,94,0.12)" }}>
                <Smartphone size={18} color="#22c55e" />
              </div>
              <h3 style={cardTitle}>WhatsApp Support</h3>
            </div>
            <label style={fieldWrap}>
              <span style={fieldLabel}>WhatsApp Number</span>
              <input
                style={fieldInput}
                type="text"
                value={form.whatsapp_number || ""}
                placeholder="Enter WhatsApp number"
                onChange={(e) => setForm((c) => ({ ...c, whatsapp_number: e.target.value }))}
              />
            </label>
            <p style={hint}>Enter 10 digit number or full 91 prefix. Users will see this as a support button.</p>
          </section>

          {/* Password */}
          <section style={card}>
            <div style={cardHead}>
              <div style={{ ...cardIcon, background: "rgba(239,68,68,0.1)" }}>
                <Lock size={18} color="#ef4444" />
              </div>
              <h3 style={cardTitle}>Admin Password</h3>
            </div>
            <Field label="Current Password" type="password" value={passwordForm.current_password} onChange={(v) => setPasswordForm((c) => ({ ...c, current_password: v }))} />
            <Field label="New Password" type="password" value={passwordForm.new_password} onChange={(v) => setPasswordForm((c) => ({ ...c, new_password: v }))} />
            <Field label="Confirm New Password" type="password" value={passwordForm.confirm_password} onChange={(v) => setPasswordForm((c) => ({ ...c, confirm_password: v }))} />
            <button type="button" style={pwBtn} onClick={changePassword} disabled={pwSaving}>
              {pwSaving ? "Changing..." : "Change Password"}
            </button>
          </section>

          {/* Running Ticker Notice */}
          <section style={card}>
            <div style={cardHead}>
              <div style={{ ...cardIcon, background: "rgba(249,115,22,0.1)" }}>
                <Type size={18} color="#f97316" />
              </div>
              <h3 style={cardTitle}>Header Ticker Text</h3>
            </div>
            <div style={fieldWrap}>
              <span style={fieldLabel}>Running Notice (scrolling text in header)</span>
              <textarea
                style={{ ...fieldInput, height: 80, padding: "10px 14px", resize: "vertical" }}
                value={form.home_running_notice || ""}
                placeholder="e.g. Matka kabhi bhi aapka OTP nahi mangta. | Play safe | Fast Results"
                onChange={(e) => setForm((c) => ({ ...c, home_running_notice: e.target.value }))}
              />
            </div>
            <p style={hint}>This text scrolls in the orange/purple ticker at the top of the homepage. Save Settings to apply.</p>
          </section>

          {/* App Visibility */}
          <section style={card}>
            <div style={cardHead}>
              <div style={{ ...cardIcon, background: "rgba(99,102,241,0.1)" }}>
                <Eye size={18} color="#6366f1" />
              </div>
              <h3 style={cardTitle}>App Visibility</h3>
            </div>
            <Toggle
              label="Casino Card on Dashboard"
              hint="Show or hide the Casino card on the user dashboard"
              value={form.show_casino_card === "1"}
              onChange={(v) => setForm((c) => ({ ...c, show_casino_card: v ? "1" : "0" }))}
            />
            <Toggle
              label="Demo Login Button"
              hint="Show or hide the Demo Login button on the login page"
              value={form.show_demo_login === "1"}
              onChange={(v) => setForm((c) => ({ ...c, show_demo_login: v ? "1" : "0" }))}
            />
            <p style={hint}>Changes take effect after saving. Users will see the updated dashboard immediately.</p>
          </section>

          {/* Google Authenticator */}
          <section style={card}>
            <div style={cardHead}>
              <div style={{ ...cardIcon, background: "rgba(79,70,229,0.1)" }}>
                <ShieldCheck size={18} color="#4f46e5" />
              </div>
              <h3 style={cardTitle}>Admin Google Authenticator (2FA)</h3>
            </div>
            {gaLoading ? (
              <p style={hint}>Loading 2FA settings...</p>
            ) : googleAuth?.enabled ? (
              <>
                <p style={hint}>Scan this QR code in Google Authenticator app to enable 2FA for admin login. Keep it secret.</p>
                <div style={qrWrap}>
                  <img src={googleAuth.qr_url} alt="Admin 2FA QR Code" style={qrImg} />
                </div>
                <div style={secretBox}>
                  <span style={secretLabel}>Secret Key:</span>
                  <code style={secretCode}>{googleAuth.secret}</code>
                </div>
                <button type="button" style={regenBtn} onClick={regenerateGoogleAuth} disabled={gaRegen}>
                  <RefreshCw size={14} />
                  {gaRegen ? "Regenerating..." : "Regenerate QR (Invalidates Old)"}
                </button>
                <button type="button" style={{ ...regenBtn, marginTop: 8, background: "#fee2e2", color: "#dc2626", border: "1px solid #fca5a5" }} onClick={disableGoogleAuth} disabled={gaRegen}>
                  {gaRegen ? "Please wait..." : "Disable 2FA"}
                </button>
              </>
            ) : (
              <>
                <p style={hint}>2FA is currently disabled. Click below to set up Google Authenticator for admin login.</p>
                <button type="button" style={pwBtn} onClick={setupGoogleAuth} disabled={gaRegen}>
                  {gaRegen ? "Setting up..." : "Enable 2FA / Set Up QR"}
                </button>
              </>
            )}
          </section>
        </div>
      </div>
    </AdminLayout>
  );
}


function Field({ label, value, onChange, type = "text", placeholder = "" }) {
  return (
    <label style={fieldWrap}>
      <span style={fieldLabel}>{label}</span>
      <input
        type={type}
        style={fieldInput}
        value={value || ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

function Toggle({ label, hint, value, onChange }) {
  return (
    <div style={toggleRow}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={toggleLabel}>{label}</div>
        {hint && <div style={toggleHint}>{hint}</div>}
      </div>
      <button
        type="button"
        style={value ? toggleTrackOn : toggleTrackOff}
        onClick={() => onChange(!value)}
        aria-label={label}
      >
        <span style={{ ...toggleThumb, transform: value ? "translateX(20px)" : "translateX(2px)" }} />
      </button>
    </div>
  );
}

function FieldSelect({ label, value, onChange, options }) {
  return (
    <label style={fieldWrap}>
      <span style={fieldLabel}>{label}</span>
      <select style={fieldInput} value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </label>
  );
}

const page = { display: "grid", gap: 20 };
const pageHeader = { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 };
const title = { margin: 0, fontSize: 22, fontWeight: 800, color: "#1e1b4b" };
const subtitle = { margin: "4px 0 0", fontSize: 13, color: "#64748b" };
const saveBtn = { height: 44, padding: "0 22px", border: 0, background: "linear-gradient(135deg, #f97316, #ea580c)", color: "#fff", borderRadius: 12, display: "flex", alignItems: "center", gap: 8, fontWeight: 700, fontSize: 14, cursor: "pointer", flexShrink: 0, boxShadow: "0 4px 14px rgba(249,115,22,0.3)" };

const grid = { display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 20 };
const card = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 18, padding: "22px 20px", display: "grid", gap: 14, boxShadow: "0 2px 8px rgba(0,0,0,0.04)" };
const cardHead = { display: "flex", alignItems: "center", gap: 12 };
const cardIcon = { width: 38, height: 38, borderRadius: 10, display: "grid", placeItems: "center", flexShrink: 0 };
const cardTitle = { margin: 0, fontSize: 15, fontWeight: 800, color: "#1e1b4b" };

const fieldWrap = { display: "grid", gap: 6 };
const fieldLabel = { color: "#475569", fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" };
const fieldInput = { height: 44, border: "1.5px solid #e2e8f0", borderRadius: 10, padding: "0 14px", fontSize: 14, color: "#0f172a", outline: "none", background: "#f8fafc", boxSizing: "border-box", width: "100%" };
const hint = { margin: "-4px 0 0", color: "#94a3b8", fontSize: 12, lineHeight: 1.5 };
const pwBtn = { height: 44, padding: "0 20px", border: 0, background: "linear-gradient(135deg, #1e1b4b, #4f46e5)", color: "#fff", borderRadius: 11, fontWeight: 700, fontSize: 14, cursor: "pointer", justifySelf: "start" };
const timeRow = { display: "flex", gap: 12 };
const dayGrid = { display: "flex", gap: 8, flexWrap: "wrap", marginTop: 4 };
const dayBtn = { height: 36, padding: "0 14px", border: "1.5px solid #e2e8f0", borderRadius: 999, background: "#f8fafc", color: "#475569", fontWeight: 700, fontSize: 13, cursor: "pointer" };
const dayActive = { ...dayBtn, background: "linear-gradient(135deg, #4f46e5, #312e81)", borderColor: "#4f46e5", color: "#fff" };

const qrWrap    = { display: "flex", justifyContent: "center" };
const qrImg     = { width: 180, height: 180, border: "3px solid #e2e8f0", borderRadius: 12 };
const secretBox = { display: "flex", alignItems: "center", gap: 8, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "8px 12px", flexWrap: "wrap" };
const secretLabel= { fontSize: 12, fontWeight: 700, color: "#475569" };
const secretCode = { fontSize: 13, fontWeight: 800, color: "#1e1b4b", letterSpacing: "0.05em", wordBreak: "break-all" };
const regenBtn  = { display: "flex", alignItems: "center", gap: 6, height: 40, padding: "0 16px", border: "1.5px solid #ef4444", borderRadius: 10, background: "#fff", color: "#ef4444", fontWeight: 700, fontSize: 13, cursor: "pointer", justifySelf: "start" };

const toggleRow   = { display: "flex", alignItems: "center", gap: 14, padding: "4px 0" };
const toggleLabel = { fontSize: 14, fontWeight: 700, color: "#1e1b4b" };
const toggleHint  = { fontSize: 12, color: "#94a3b8", marginTop: 2, lineHeight: 1.4 };
const toggleTrackOn  = { flexShrink: 0, width: 44, height: 26, borderRadius: 999, border: 0, background: "linear-gradient(135deg,#22c55e,#16a34a)", cursor: "pointer", position: "relative", transition: "background 0.2s", display: "flex", alignItems: "center" };
const toggleTrackOff = { ...toggleTrackOn, background: "#e2e8f0" };
const toggleThumb = { width: 22, height: 22, borderRadius: "50%", background: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,0.2)", display: "block", transition: "transform 0.2s", position: "absolute" };

export default AdminSettings;
